import { euro } from "../../lib/invoices";
import { eintragZeilenzahl, type AblageEintrag } from "../../lib/invoiceClipboard";

/**
 * Die Zwischenablage als seitliche Einblendung.
 *
 * Auf dem Desktop fährt sie von rechts ein, auf dem Telefon als Blatt von
 * unten. Sie liegt über dem Editor statt daneben: der Belegeditor braucht
 * seine Breite für die Positionszeilen, und die Ablage ist ein Werkzeug für
 * einen Moment, kein Dauerbegleiter.
 */

export type ClipboardPanelProps = {
  eintraege: AblageEintrag[];
  offen: boolean;
  onSchliessen: () => void;
  onEinfuegen: (eintraege: AblageEintrag[]) => void;
  onEntfernen: (id: string) => void;
  onLeeren: () => void;
};

/** Netto eines Eintrags — bei einem Block die Summe seiner Positionen. */
function eintragNetto(eintrag: AblageEintrag): number {
  const zeilen = eintrag.art === "block" ? eintrag.zeilen : [eintrag.kopf];
  return zeilen.reduce((summe, line) => (
    line.line_kind === "position"
      ? summe + (Number(line.quantity) || 0) * (Number(line.unit_price_net) || 0)
      : summe
  ), 0);
}

export default function InvoiceClipboardPanel({
  eintraege, offen, onSchliessen, onEinfuegen, onEntfernen, onLeeren,
}: ClipboardPanelProps) {
  if (!offen) return null;

  const zeilenGesamt = eintraege.reduce((summe, e) => summe + eintragZeilenzahl(e), 0);

  return (
    <div
      className="invoice-ablage-backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onSchliessen(); }}
    >
      <aside className="invoice-ablage" role="dialog" aria-modal="true" aria-labelledby="ablage-titel">
        <header className="invoice-ablage-head">
          <div>
            <p className="invoice-kicker">Zwischenablage</p>
            <h2 id="ablage-titel">
              {eintraege.length === 0
                ? "Noch nichts gesammelt"
                : `${eintraege.length} ${eintraege.length === 1 ? "Eintrag" : "Einträge"}`}
            </h2>
          </div>
          <button type="button" className="invoice-button" onClick={onSchliessen}>Schließen</button>
        </header>

        {eintraege.length === 0 ? (
          <p className="invoice-ablage-leer">
            Wähle im Beleg Positionen oder ganze Blöcke aus und lege sie hier ab. Die Ablage
            bleibt beim Wechsel in einen anderen Beleg erhalten — sie liegt in diesem Browser,
            nicht auf dem Server. Für etwas, das du dauerhaft wiederverwenden willst, ist die
            Bausteinbibliothek der richtige Ort.
          </p>
        ) : (
          <>
            <div className="invoice-ablage-aktionen">
              <button
                type="button"
                className="invoice-button primary"
                onClick={() => onEinfuegen(eintraege)}
              >
                Alles einfügen ({zeilenGesamt} {zeilenGesamt === 1 ? "Zeile" : "Zeilen"})
              </button>
              <button type="button" className="invoice-button" onClick={onLeeren}>Leeren</button>
            </div>

            <ul className="invoice-ablage-liste">
              {eintraege.map((eintrag) => (
                <li key={eintrag.id} className={eintrag.art === "block" ? "is-block" : ""}>
                  <div className="invoice-ablage-zeile">
                    <span className="invoice-ablage-text">
                      <strong>
                        {eintrag.art === "block" && <i className="invoice-catalog-tag">Block</i>}
                        {eintrag.kopf.title.trim() || "Ohne Bezeichnung"}
                      </strong>
                      <small>
                        {eintrag.art === "block"
                          ? `${eintrag.zeilen.length} ${eintrag.zeilen.length === 1 ? "Position" : "Positionen"}`
                          : `${eintrag.kopf.quantity} ${eintrag.kopf.unit_label}`}
                        {" · "}{euro(eintragNetto(eintrag))}
                        {" · aus "}{eintrag.quelle}
                      </small>
                    </span>
                    <span className="invoice-ablage-zeile-tools">
                      <button
                        type="button"
                        className="invoice-button"
                        onClick={() => onEinfuegen([eintrag])}
                        title="Nur diesen Eintrag einfügen"
                      >
                        Einfügen
                      </button>
                      <button
                        type="button"
                        className="invoice-line-remove"
                        onClick={() => onEntfernen(eintrag.id)}
                        aria-label={`„${eintrag.kopf.title}" aus der Ablage entfernen`}
                      >
                        ×
                      </button>
                    </span>
                  </div>
                  {eintrag.art === "block" && eintrag.zeilen.length > 0 && (
                    <ul className="invoice-ablage-unterzeilen">
                      {eintrag.zeilen.slice(0, 4).map((line) => (
                        <li key={line.id}>{line.title.trim() || "Ohne Bezeichnung"}</li>
                      ))}
                      {eintrag.zeilen.length > 4 && (
                        <li className="is-mehr">und {eintrag.zeilen.length - 4} weitere</li>
                      )}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
